import { AnimatePresence, motion } from 'framer-motion'
import { AlertTriangle, Check, Loader2, Lock, Mail, Pencil, Trash2, X } from 'lucide-react'
import React, { useContext, useState } from 'react'
import { FaToggleOff, FaToggleOn, FaWhatsapp } from 'react-icons/fa'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './DRCEachReceiver.module.css'

const ROLES = [
    { value: 'owner',     label: 'Owner' },
    { value: 'manager',   label: 'Manager' },
    { value: 'accountant',label: 'Accountant' },
    { value: 'other',     label: 'Other' },
]

const DRCEachReceiver = ({ item, getReceiversForDailyReports }) => {
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = (() => { try { return asCtx?.activeStore && asCtx.activeStore.trim() !== '' ? JSON.parse(asCtx.activeStore) : null } catch { return null } })()

    const [isDeleting, setIsDeleting]   = useState(false)
    const [isToggling, setIsToggling]   = useState(false)
    const [isEditing, setIsEditing]     = useState(false)
    const [isSaving, setIsSaving]       = useState(false)
    const [editName, setEditName]       = useState(item?.receiverName ?? '')
    const [editRole, setEditRole]       = useState(item?.receiverRole ?? 'owner')
    const [localIsActive, setLocalIsActive] = useState(item?.isActive ?? 0)
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
    const [showLastReceiverWarning, setShowLastReceiverWarning] = useState(false)

    const isActive    = localIsActive === 1
    const isWhatsApp  = item?.deliveryPlatform === 'WhatsApp'
    const displayAddr = `${item?.countryCode && item.countryCode !== 'null' ? item.countryCode + ' ' : ''}${item?.receiver}`

    const openEdit = () => {
        setEditName(item?.receiverName ?? '')
        setEditRole(item?.receiverRole ?? 'owner')
        setIsEditing(true)
    }
    const cancelEdit = () => setIsEditing(false)

    const saveReceiver = async () => {
        setIsSaving(true)
        const changes = { receiverName: editName.trim() || null, receiverRole: editRole }
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsUpdateReceiver', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    receiverIdentifier: item.receiverIdentifier,
                    ...changes,
                }),
            })
            if (!res.ok) return
            const data = await res.json()
            if (data?.status === 'success') {
                getReceiversForDailyReports()
                toast.success('Receiver updated.')
                setIsEditing(false)
            } else {
                if (data?.message === 'tokenExpired') authCtx.logout()
                else toast.error(data?.message === 'receiverNotFound' ? 'Receiver not found.' : 'Failed to update.')
            }
        } catch { toast.error('Something went wrong.') } finally {
            setIsSaving(false)
        }
    }

    const deleteReceiverForDailyReports = async () => {
        setIsDeleting(true)
        setShowDeleteConfirm(false)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsDeleteReceiver', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier, receiverIdentifier: item.receiverIdentifier }),
            })
            if (!res.ok) { toast.error('Something went wrong.'); return }
            const data = await res.json()
            if (data?.status === 'success') {
                getReceiversForDailyReports()
                toast.success('Receiver removed.')
            } else {
                if (data?.message === 'tokenExpired') authCtx.logout()
                else if (data?.message === 'lastReceiverBlock') setShowLastReceiverWarning(true)
                else toast.error('Failed to delete receiver.')
            }
        } catch { toast.error('Something went wrong.') } finally {
            setIsDeleting(false)
        }
    }

    const toggleReceiverForDailyReports = async (k) => {
        const currentIsActive = localIsActive
        const newIsActive = currentIsActive === 1 ? 0 : 1

        // Optimistic update
        setLocalIsActive(newIsActive)
        setIsToggling(true)
        try {
            const res = await fetch(BASE_URL_API + 'dailyReportsToggleReceiver', {
                method: 'POST',
                body: JSON.stringify({ token: authCtx.token, storeIdentifier: activeStore?.storeIdentifier, receiverIdentifier: k, isActive: newIsActive }),
            })
            if (!res.ok) {
                setLocalIsActive(currentIsActive)
                return
            }
            const data = await res.json()
            if (data?.status === 'success') {
                toast.success('Receiver status updated.')
            } else {
                // Revert optimistic update on failure
                setLocalIsActive(currentIsActive)
                if (data?.message === 'tokenExpired') authCtx.logout()
                else toast.error('Failed to update receiver status.')
            }
        } catch {
            // Revert optimistic update on error
            setLocalIsActive(currentIsActive)
        } finally {
            setIsToggling(false)
        }
    }

    return (
        <>
        <div className={`${styles.row} ${!isActive ? styles.rowInactive : ''} ${isEditing ? styles.rowEditing : ''}`}>

            {/* ── Top: icon + info + actions ── */}
            <div className={styles.rowMain}>
                <div className={styles.left}>
                    <div className={styles.platformIcon}>
                        {isWhatsApp
                            ? <FaWhatsapp size={16} color={isActive ? '#25D366' : '#94a3b8'} />
                            : <Mail size={16} color={isActive ? '#2463EB' : '#94a3b8'} />}
                    </div>
                    <div className={styles.info}>
                        {/* Address — always read-only */}
                        <div className={styles.addrRow}>
                            <Lock size={11} className={styles.lockIcon} />
                            <span className={styles.receiver}>{displayAddr}</span>
                        </div>
                        {/* View-mode meta */}
                        {!isEditing && (
                            <div className={styles.meta}>
                                {isActive
                                    ? <span className={styles.badgeActive}>Active</span>
                                    : <span className={styles.badgeInactive}>Inactive</span>}
                                {item?.receiverRole && (
                                    <span className={styles.badgeRole}>{item.receiverRole}</span>
                                )}
                                {item?.receiverName && (
                                    <span className={styles.nameTag}>{item.receiverName}</span>
                                )}
                                <span className={styles.since}>since {item?.createdOn?.split(' ')[0]}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Actions */}
                <div className={styles.actions}>
                    {isEditing ? (
                        <>
                            <button
                                className={styles.saveBtn}
                                onClick={saveReceiver}
                                disabled={isSaving}
                                title="Save"
                                type="button"
                            >
                                {isSaving
                                    ? <Loader2 size={14} className={styles.spinIcon} />
                                    : <Check size={14} />}
                            </button>
                            <button
                                className={styles.cancelBtn}
                                onClick={cancelEdit}
                                disabled={isSaving}
                                title="Cancel"
                                type="button"
                            >
                                <X size={14} />
                            </button>
                        </>
                    ) : (
                        <button
                            className={styles.editBtn}
                            onClick={openEdit}
                            title="Edit name / role"
                            type="button"
                        >
                            <Pencil size={13} />
                        </button>
                    )}
                    <button
                        className={styles.toggleBtn}
                        onClick={() => !isToggling && toggleReceiverForDailyReports(item.receiverIdentifier)}
                        title={isActive ? 'Deactivate' : 'Activate'}
                        type="button"
                    >
                        {isToggling
                            ? <Loader2 size={22} className={styles.spinIcon} color="#94a3b8" />
                            : isActive
                                ? <FaToggleOn size={26} color="#2463EB" />
                                : <FaToggleOff size={26} color="#cbd5e1" />}
                    </button>
                    <button
                        className={styles.deleteBtn}
                        onClick={() => !isDeleting && setShowDeleteConfirm(true)}
                        disabled={isDeleting}
                        title="Remove receiver"
                        type="button"
                    >
                        {isDeleting
                            ? <Loader2 size={15} className={styles.spinIcon} />
                            : <Trash2 size={15} />}
                    </button>
                </div>
            </div>

            {/* ── Edit panel ── */}
            {isEditing && (
                <div className={styles.editPanel}>
                    <div className={styles.editField}>
                        <label className={styles.editLabel}>Display Name</label>
                        <input
                            className={styles.editInput}
                            type="text"
                            placeholder="e.g. Navneet, Store Manager…"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            autoFocus
                            onKeyDown={(e) => e.key === 'Enter' && saveReceiver()}
                        />
                    </div>
                    <div className={styles.editField}>
                        <label className={styles.editLabel}>Role</label>
                        <select
                            className={styles.editSelect}
                            value={editRole}
                            onChange={(e) => setEditRole(e.target.value)}
                        >
                            {ROLES.map((r) => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                            ))}
                        </select>
                    </div>
                </div>
            )}
        </div>

            {/* ── Delete Confirmation Dialog ── */}
            <AnimatePresence>
                {showDeleteConfirm && (
                    <motion.div
                        className={styles.modalBackdrop}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !isDeleting && setShowDeleteConfirm(false)}
                    >
                        <motion.div
                            className={styles.modalCard}
                            initial={{ opacity: 0, y: 14, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 14, scale: 0.98 }}
                            transition={{ duration: 0.18 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={styles.modalHeader}>
                                <h3>Remove Receiver?</h3>
                                <button
                                    className={styles.modalCloseBtn}
                                    onClick={() => setShowDeleteConfirm(false)}
                                    disabled={isDeleting}
                                    title="Close"
                                    type="button"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                            <p>
                                <strong>{displayAddr}</strong> will no longer receive daily reports.
                                This action cannot be undone.
                            </p>
                            <div className={styles.modalActions}>
                                <button
                                    className={styles.modalCancelBtn}
                                    onClick={() => setShowDeleteConfirm(false)}
                                    disabled={isDeleting}
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    className={styles.modalConfirmBtn}
                                    onClick={deleteReceiverForDailyReports}
                                    disabled={isDeleting}
                                    type="button"
                                >
                                    {isDeleting
                                        ? <><Loader2 size={13} className={styles.spinIcon} /> Removing…</>
                                        : <><Trash2 size={13} /> Remove Receiver</>}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Last Receiver Block Warning ── */}
            <AnimatePresence>
                {showLastReceiverWarning && (
                    <motion.div
                        className={styles.modalBackdrop}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowLastReceiverWarning(false)}
                    >
                        <motion.div
                            className={`${styles.modalCard} ${styles.warningCard}`}
                            initial={{ opacity: 0, y: 14, scale: 0.98 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 14, scale: 0.98 }}
                            transition={{ duration: 0.18 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className={styles.modalHeader}>
                                <div className={styles.warningTitleRow}>
                                    <AlertTriangle size={17} className={styles.warningIcon} />
                                    <h3>Can't Remove Last Receiver</h3>
                                </div>
                                <button
                                    className={styles.modalCloseBtn}
                                    onClick={() => setShowLastReceiverWarning(false)}
                                    title="Close"
                                    type="button"
                                >
                                    <X size={14} />
                                </button>
                            </div>
                            <p className={styles.warningBody}>
                                Auto-reports are currently <strong>enabled</strong>. You must disable auto-reporting
                                before removing the last receiver.
                            </p>
                            <div className={styles.modalActions}>
                                <button
                                    className={styles.modalCancelBtn}
                                    onClick={() => setShowLastReceiverWarning(false)}
                                    type="button"
                                >
                                    Dismiss
                                </button>
                                <button
                                    className={styles.warningCtaBtn}
                                    onClick={() => {
                                        setShowLastReceiverWarning(false)
                                        document.getElementById('daily-reports-settings')?.scrollIntoView({ behavior: 'smooth' })
                                    }}
                                    type="button"
                                >
                                    Disable Auto-Report
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    )
}

export default DRCEachReceiver
